import { useSearchParams } from "react-router-dom";
import { ProductCard } from "@/components/shop/ProductCard";
import { ShowMore, useShowMore } from "@/components/shop/ShopBits";
import { getCategories, getCategory, getCollection, getCollections, getPattern, getProducts } from "@/lib/catalog";
import { usePageMeta } from "@/lib/seo";
import { patternBg } from "@/lib/patternBg";
import { familyNames, familyOf } from "@/lib/patternFamilies";
import { ChevronDown, X } from "lucide-react";

const Chip = ({ active, onClick, children, testId }) => (
  <button type="button" data-testid={testId} aria-pressed={active} onClick={onClick} className={`min-h-[44px] shrink-0 rounded-full border px-4 text-sm transition-colors duration-300 sm:px-5 ${active ? "border-bordeaux bg-bordeaux text-white" : "border-ink/15 text-ink hover:border-bordeaux/50"}`}>
    {children}
  </button>
);

const SORTS = [
  ["destaques", "Destaques"],
  ["preco-asc", "Preço: do mais baixo"],
  ["preco-desc", "Preço: do mais alto"],
  ["nome", "Nome: A a Z"],
];
const priceOf = (p) => (typeof p.price === "number" ? p.price : Number.POSITIVE_INFINITY);

const Select = ({ label, value, onChange, children, testId, className = "" }) => (
  <label className={`relative inline-flex min-w-0 items-center ${className}`}>
    <span className="sr-only">{label}</span>
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId} className="min-h-[44px] w-full min-w-0 cursor-pointer appearance-none rounded-full border border-ink/15 bg-ivory/80 py-0 pl-4 pr-10 text-sm text-ink outline-none transition-colors hover:border-bordeaux/40 focus-visible:border-bordeaux">
      {children}
    </select>
    <ChevronDown className="pointer-events-none absolute right-3.5 h-4 w-4 text-taupe" aria-hidden="true" />
  </label>
);

export default function ShopPage() {
  const [params, setParams] = useSearchParams();
  const current = getCategory(params.get("categoria"));
  const pattern = getPattern(params.get("tecido"));
  const collection = getCollection(params.get("colecao"));
  const FAMILY_NAMES = familyNames().filter((n) => n !== "Saquinhos" && n !== "Outros");
  const family = FAMILY_NAMES.includes(params.get("familia")) ? params.get("familia") : "";
  const sort = SORTS.some(([k]) => k === params.get("ordem")) ? params.get("ordem") : "destaques";
  const filtered = getProducts(current?.id).filter((p) => (!pattern || p.patterns.includes(pattern.id)) && (!collection || collection.productSlugs.includes(p.slug))
    && (!family || p.patterns.some((id) => familyOf(id) === family)));
  const list = sort === "destaques" ? filtered : [...filtered].sort((a, b) => (sort === "nome" ? a.name.localeCompare(b.name, "pt") : sort === "preco-asc" ? priceOf(a) - priceOf(b) : (priceOf(b) === Infinity ? -1 : priceOf(a) === Infinity ? 1 : priceOf(b) - priceOf(a))));
  const { shown, remaining, more, step } = useShowMore(list, 12, params.toString());
  const activeFilters = (family ? 1 : 0) + (pattern ? 1 : 0) + (collection ? 1 : 0);
  usePageMeta(current ? current.name : "Loja", current?.intro ?? "Babetes, porta-chupetas, porta-fraldas e tapa-fraldas feitos à mão pela Alexandra.");
  // Cada filtro muda só o seu parâmetro; os outros mantêm-se.
  const setFilter = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next);
  };
  const select = (id) => setFilter("categoria", id);
  const clearPattern = () => setFilter("tecido", null);

  return (
    <div className="relative isolate -mt-[60px] overflow-hidden bg-[linear-gradient(180deg,#F1E9DD_0%,#F5EFE5_32%,#F9F5EE_70%,rgb(var(--bb-ivory))_100%)]" data-testid="shop-page">
      {/* tecido vichy bege de fundo, a desvanecer para baixo */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-[560px] [mask-image:linear-gradient(180deg,#000_30%,transparent)]" style={patternBg("vichy-bege", "241,233,221", 0.72, 280)} />
      <span aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-[64px] -z-10 h-[2px] bg-[repeating-linear-gradient(90deg,rgba(117,67,75,.28)_0_7px,transparent_7px_14px)]" />
    <div className="bb-container pb-10 pt-[80px] sm:pt-[124px]">
      <p className="bb-eyebrow mb-2 sm:mb-4">Loja</p>
      <h1 data-testid="shop-title" className="font-serif text-[2.1rem] font-normal leading-none tracking-tight sm:text-6xl lg:text-7xl">{current ? current.name : "Todas as peças"}</h1>
      <p className="mt-2 line-clamp-2 max-w-xl text-sm text-taupe sm:mt-5 sm:line-clamp-none sm:text-base">{current?.intro ?? "Escolhe a categoria e depois o padrão, dentro de cada peça."}</p>
      <div className="sticky top-[var(--hdr,60px)] z-20 transition-[top] duration-300 -mx-5 mt-6 bg-[#F4EEE4]/90 px-5 py-2 backdrop-blur-xl sm:-mx-8 sm:mt-10 sm:px-8 sm:py-3">
        <div className="flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none]" role="group" aria-label="Filtrar por categoria">
          <Chip active={!current} onClick={() => select(null)} testId="shop-filter-all">Todas</Chip>
          {getCategories().map((c) => (
            <Chip key={c.id} active={current?.id === c.id} onClick={() => select(c.id)} testId={`shop-filter-${c.id}`}>{c.name}</Chip>
          ))}
        </div>
      </div>
      {getCollections().length > 0 && (
        <div className="mt-3 flex items-center gap-2 overflow-x-auto pb-1 [scrollbar-width:none]" role="group" aria-label="Filtrar por coleção" data-testid="shop-collections">
          <span className="shrink-0 text-xs font-semibold uppercase tracking-[0.16em] text-taupe-strong">Coleções</span>
          <Chip active={!collection} onClick={() => setFilter("colecao", null)} testId="shop-collection-all">Todas</Chip>
          {getCollections().map((c) => <Chip key={c.slug} active={collection?.slug === c.slug} onClick={() => setFilter("colecao", c.slug)} testId={`shop-collection-${c.slug}`}>{c.label}</Chip>)}
        </div>
      )}
      <div className="mt-3 flex flex-wrap items-center gap-2 sm:mt-5 sm:gap-2.5" role="group" aria-label="Filtrar e ordenar" data-testid="shop-toolbar">
        <Select label="Tecido" value={family} onChange={(v) => setFilter("familia", v || null)} testId="shop-filter-family" className="order-1 flex-1 sm:order-none sm:flex-none">
          <option value="">Tecidos</option>
          {FAMILY_NAMES.map((n) => <option key={n} value={n}>{n}</option>)}
        </Select>
        {activeFilters > 0 && (
          <button type="button" data-testid="shop-clear-filters" onClick={() => setParams(current ? { categoria: current.id } : {})} className="order-4 inline-flex min-h-[44px] items-center gap-1.5 px-2 text-sm font-medium text-bordeaux underline-offset-4 hover:underline sm:order-none"><X className="h-3.5 w-3.5" aria-hidden="true" /> Limpar filtros</button>
        )}
        <div className="order-2 flex min-w-0 flex-1 items-center gap-2 sm:order-last sm:ml-auto sm:flex-none">
          <span className="hidden text-xs font-semibold uppercase tracking-[0.16em] text-taupe-strong sm:inline">Ordenar</span>
          <Select label="Ordenar por" value={sort} onChange={(v) => setFilter("ordem", v === "destaques" ? null : v)} testId="shop-sort" className="flex-1 sm:flex-none">
            {SORTS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </Select>
        </div>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-2 sm:mt-4">
        <p className="text-sm text-taupe" data-testid="shop-count">{list.length} {list.length === 1 ? "modelo" : "modelos"}</p>
        {collection && (
          <button type="button" data-testid="shop-filter-collection-remove" onClick={() => setFilter("colecao", null)} className="inline-flex min-h-[36px] items-center gap-2 rounded-full bg-rose-soft px-3.5 text-xs font-medium text-bordeaux transition-colors hover:bg-rose/40" aria-label={`Remover filtro: ${collection.label}`}>
            {collection.label} <span aria-hidden="true">×</span>
          </button>
        )}
        {pattern && (
          <button type="button" data-testid="shop-filter-pattern-remove" onClick={clearPattern} className="inline-flex min-h-[36px] items-center gap-2 rounded-full bg-linen px-3.5 text-xs font-medium text-ink transition-colors hover:bg-linen/70" aria-label={`Remover filtro: tecido ${pattern.name}`}>
            Tecido: {pattern.name} <span aria-hidden="true">×</span>
          </button>
        )}
      </div>
      {list.length === 0 && (
        <p className="mt-10 rounded-2xl bg-ivory/80 px-5 py-8 text-center text-taupe" data-testid="shop-empty">Nenhuma peça com estes filtros. <button type="button" onClick={() => setParams(current ? { categoria: current.id } : {})} className="font-medium text-bordeaux underline underline-offset-4">Limpar filtros</button></p>
      )}
      <div className="mt-4 grid grid-cols-2 gap-x-3 gap-y-8 sm:mt-6 sm:gap-x-6 sm:gap-y-10 lg:grid-cols-3 xl:grid-cols-4" data-testid="shop-grid">
        {shown.map((p) => (
          <ProductCard key={`${current?.id}-${p.slug}`} product={p} patternId={pattern?.id} />
        ))}
      </div>
      <ShowMore remaining={remaining} step={step} onClick={more} testId="shop-show-more" />
    </div>
    </div>
  );
}